import React, { useCallback, useEffect, useState } from "react";
import { api, extractError } from "../lib/api";
import { todayIsoDate } from "../lib/purchaseListMeta";
import { hasPermission, useAuth } from "../context/AuthContext";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Pill } from "./Ui";
import { toast } from "sonner";

/**
 * Assign labour contractor to a purchase/unloading record.
 * Used on Operations unload form and Recorded unloading editor (register).
 */
export default function LabourContractorPicker({
  purchaseId,
  unloading,
  partyId: partyIdProp,
  onPartyIdChange,
  onSaved,
  showHelper = true,
}) {
  const { user } = useAuth();
  const [contractors, setContractors] = useState([]);
  const [partyId, setPartyId] = useState("");
  const [activeContract, setActiveContract] = useState(null);
  const [saving, setSaving] = useState(false);

  const canAssign = hasPermission(user, "labour.work.record");

  useEffect(() => {
    if (!canAssign) return;
    api
      .get("/parties/bootstrap", { params: { kind: "LABOUR_CONTRACTOR", active: "1" } })
      .then((r) => setContractors(r.data?.results || []))
      .catch(() => setContractors([]));
  }, [canAssign]);

  const syncPartyId = partyIdProp ?? unloading?.labour_contractor_party_id;
  useEffect(() => {
    setPartyId(syncPartyId ? String(syncPartyId) : "");
  }, [syncPartyId, purchaseId]);

  const loadActiveContract = useCallback(async (pid) => {
    if (!pid) {
      setActiveContract(null);
      return null;
    }
    try {
      const { data } = await api.get("/labour-contracts/active", {
        params: { party_id: pid, work_date: todayIsoDate() },
      });
      if (!data?.active) {
        setActiveContract(null);
        return null;
      }
      const { active: _active, ...contract } = data;
      setActiveContract(contract);
      return contract;
    } catch {
      setActiveContract(null);
      return null;
    }
  }, []);

  useEffect(() => {
    if (partyId) loadActiveContract(partyId);
    else setActiveContract(null);
  }, [partyId, loadActiveContract]);

  const saveContractor = async (nextPartyId) => {
    if (!purchaseId || !nextPartyId) return false;
    setSaving(true);
    try {
      const { data } = await api.patch(`/unloading/purchase/${purchaseId}/labour-contract`, {
        labour_contractor_party_id: Number(nextPartyId),
      });
      onPartyIdChange?.(String(nextPartyId));
      onSaved?.(data);
      if (data.sync_warning) {
        toast.warning(`Contractor saved — labour sync: ${data.sync_warning}`);
      } else if (!data.labour_contract_id) {
        toast.warning(
          "Contractor saved, but no active labour contract covers today. Add or extend a contract under Labour Contracts.",
        );
      }
      return true;
    } catch (err) {
      const message = extractError(err);
      const isNetwork = !err?.response && (message === "Network Error" || err?.code === "ERR_NETWORK");
      if (err?.response?.status !== 404) {
        toast.error(
          isNetwork
            ? "Could not reach server while saving labour contractor — check connection"
            : message,
        );
      }
      return false;
    } finally {
      setSaving(false);
    }
  };

  if (!canAssign || !purchaseId) return null;

  const contractorName =
    contractors.find((p) => String(p.id) === String(partyId))?.display_name
    || unloading?.labour_contractor_name
    || "";

  return (
    <div className="rounded-md border border-border bg-card p-3 space-y-2 mb-3">
      <Label className="text-xs font-medium">Labour contractor</Label>
      {partyId && contractorName && (
        <div className="flex items-center gap-2 text-xs">
          <Pill tone="sage">{contractorName}</Pill>
        </div>
      )}
      <Select
        value={partyId}
        disabled={saving}
        onValueChange={(v) => {
          if (!v) return;
          setPartyId(v);
          loadActiveContract(v);
          saveContractor(v);
        }}
      >
        <SelectTrigger className="w-full max-w-md">
          <SelectValue placeholder="Select labour contractor" />
        </SelectTrigger>
        <SelectContent>
          {contractors.map((p) => (
            <SelectItem key={p.id} value={String(p.id)}>
              {p.display_name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {activeContract && (
        <p className="text-[10px] text-muted-foreground">
          Active contract <span className="font-mono">{activeContract.contract_number}</span>
          {" "}({activeContract.valid_from}
          {activeContract.valid_to ? ` → ${activeContract.valid_to}` : ""})
        </p>
      )}
      {partyId && !activeContract && (
        <p className="text-[10px] text-amber-700">No active contract for this contractor today.</p>
      )}
      {showHelper && (
        <p className="text-[10px] text-muted-foreground">
          Required for labour settlement and mid weigh. Pick stack / carry surcharge tiers on each unload line.
        </p>
      )}
    </div>
  );
}
